/**
 * STEP NAVIGATION COMPONENT
 * 
 * Tactical step indicator bar with progress tracking
 * Shows all 9 steps with current position and completion status
 */

import React from 'react';
import { STEP_LABELS } from '../constants/stepLabels';
import { Check, Lock } from 'lucide-react';

const StepNavigation = ({ 
  currentStep, 
  setStep, 
  completedSteps = [], 
  isLocked = false,
  theme = 'spec-ops' 
}) => {
  const isSpecOps = theme === 'spec-ops';
  
  const getStepStatus = (stepNum) => {
    if (completedSteps.includes(stepNum)) return 'completed';
    if (stepNum === currentStep) return 'current';
    if (stepNum < currentStep) return 'visited';
    return 'upcoming';
  };

  const canNavigate = (stepNum) => {
    if (isLocked) return false;
    // Can go back to any visited step, or forward to next step
    return stepNum <= currentStep || stepNum === currentStep + 1;
  };

  return (
    <div className={`
      w-full overflow-x-auto scrollbar-thin
      ${isSpecOps ? 'bg-[#0f172a] border-b border-slate-700' : 'bg-slate-50 border-b border-slate-200'}
    `}>
      <div className="flex items-center min-w-max px-4 py-3 gap-1">
        {STEP_LABELS.map((step, idx) => {
          const status = getStepStatus(step.step);
          const navigable = canNavigate(step.step);
          
          return (
            <React.Fragment key={step.step}>
              {/* Step indicator */}
              <button
                onClick={() => navigable && setStep(step.step)}
                disabled={!navigable}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded transition-all
                  ${!navigable ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                  ${status === 'current' ? (
                    isSpecOps 
                      ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-400' 
                      : 'bg-blue-500/20 border border-blue-500/50 text-blue-600'
                  ) : ''}
                  ${status === 'completed' ? (
                    isSpecOps
                      ? 'bg-emerald-500/10 text-emerald-500'
                      : 'bg-emerald-500/10 text-emerald-600'
                  ) : ''}
                  ${status === 'visited' ? (
                    isSpecOps
                      ? 'text-slate-400 hover:bg-slate-800'
                      : 'text-slate-500 hover:bg-slate-100'
                  ) : ''}
                  ${status === 'upcoming' ? (
                    isSpecOps
                      ? 'text-slate-600'
                      : 'text-slate-400'
                  ) : ''}
                `}
              >
                {/* Step number/status indicator */}
                <div className={`
                  w-6 h-6 flex items-center justify-center text-xs font-bold
                  ${status === 'completed' ? 'rounded-full bg-emerald-500 text-white' : ''}
                  ${status === 'current' ? (
                    isSpecOps 
                      ? 'rounded bg-emerald-500 text-black' 
                      : 'rounded-full bg-blue-500 text-white'
                  ) : ''}
                  ${status === 'visited' || status === 'upcoming' ? (
                    isSpecOps
                      ? 'rounded border border-slate-600 text-slate-500'
                      : 'rounded-full border border-slate-300 text-slate-400'
                  ) : ''}
                `}>
                  {status === 'completed' ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    step.step
                  )}
                </div>
                
                {/* Step name - hide on small screens for non-current */}
                <span className={`
                  text-sm font-medium whitespace-nowrap
                  ${status === 'current' ? '' : 'hidden sm:inline'}
                `}>
                  {step.shortName}
                </span>
              </button>
              
              {/* Connector line */}
              {idx < STEP_LABELS.length - 1 && (
                <div className={`
                  w-4 h-0.5 flex-shrink-0
                  ${status === 'completed' || (status === 'current' && idx < currentStep - 1) ? (
                    isSpecOps ? 'bg-emerald-500' : 'bg-emerald-500'
                  ) : (
                    isSpecOps ? 'bg-slate-700' : 'bg-slate-300'
                  )}
                `} />
              )}
            </React.Fragment>
          );
        })}
        
        {/* Lock indicator */}
        {isLocked && (
          <div className={`
            ml-4 flex items-center gap-1 px-2 py-1 rounded text-xs
            ${isSpecOps ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-600'}
          `}>
            <Lock className="w-3 h-3" />
            <span>Locked</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepNavigation;
